<template>
	<label :class="{disabled: disabled}" class="switch-wrapper">
		<span v-if="labelBefore" :class="{disabled: disabled}" class="label before">
			<span>{{ labelBefore }}</span>
			<span v-if="infoTextBefore" class="info-text">{{ infoTextBefore }}</span>
		</span>
		<input
			:id="id"
			:name="name"
			:value="enabled"
			:checked="enabled"
			:disabled="disabled"
			type="checkbox"
			:class="inputClasses"
			v-on="inputListeners"
		>
		<div :class="{ color: !noColor }" class="switch">
			<div class="switch-circle" />
		</div>
		<span v-if="label" :class="{disabled: disabled}" class="label">
			<span>{{ label }}</span>
			<span v-if="infoText" class="info-text">{{ infoText }}</span>
		</span>
	</label>
</template>

<script>
export default {
	name: 'TThreeWaySwitch',
	props: {
		/**
		 * The checked value. undefined / true / false.
		 */
		value: {
			type: Boolean,
			default: undefined,
		},
		/**
		 * Label of the switch.
		 */
		label: String,
		/**
		 * Optional label before the switch
		 */
		labelBefore: String,
		infoText: String,
		infoTextBefore: String,
		noColor: {
			type: Boolean,
			default: false
		},
		/**
		 * The id attribute of the checkbox.
		 */
		id: String,
		/**
		 * The id attribute of the radio.
		 */
		name: String,
		/**
		 * Description of the checkbox.
		 */
		description: String,
		/**
		 * Makes the input element disabled.
		 */
		disabled: {
			type: Boolean,
			default: false,
		},
	},
	computed: {
		enabled() {
			return (typeof this.value === 'boolean');
		},
		inputClasses() {
			if (this.value === false) return 'input enabled unchecked';
			else if (this.value === true) return 'input enabled checked';
			return 'input ignored';
		},
		inputListeners() {
			return {
				...this.$listeners,
				change: () => {
					let newValue;
					if (this.value === true) newValue = false;
					if (this.value === undefined) newValue = true;
					if (this.value === false) newValue = undefined;
					this.$emit('change', newValue);
				}
			};
		}
	},
};
</script>

<style lang="scss" scoped>
@import '~@/components/evergreen/scss/colors';
@import '~@/components/evergreen/scss/text';

.switch-wrapper {
	display: flex;
	cursor: pointer;
	box-sizing: border-box;
	position: relative;
	&.disabled {
		cursor: default;
	}
	&:focus-within .checkbox {
		box-shadow: 0 0 0 2px get-color($blue-scale, 'B4A'),
			inset 0 0 0 1px get-color($neutral-scale, 'N5A'),
			inset 0 -1px 1px 0 get-color($neutral-scale, 'N3A');
	}
}

.input {
	border: 0px;
	clip: rect(1px, 1px, 1px, 1px);
	height: 1px;
	overflow: hidden;
	padding: 0px;
	position: absolute;
	white-space: nowrap;
	width: 1px;
	opacity: 0;
	&.enabled + .switch {
		&.color {
			background-color: get-color($blue);
		}
	}
	&.checked + .switch > .switch-circle {
		transform: translateX(calc(200% - 4px));
		transition: transform .2s;
	}
	&.ignored + .switch > .switch-circle {
		transform: translateX(calc(100% - 2px));
		transition: transform .2s;
	}
	&:disabled + .switch {
		background-color: get-color($neutral-scale, 'N4');
	}
	&:disabled:checked + .switch {
		background-color: get-color($neutral-scale, 'N9');
	}
	&:focus + .switch {
		box-shadow: 0 0 0 3px get-color($blue-scale, 'B6A');
	}
}

.switch {
	background-color: get-color($neutral-scale, 'N5');
	border-radius: 10px;
	width: 48px;
	min-width: 48px;
	height: 20px;
	padding: 2px;
	box-sizing: border-box;
}

.switch-circle {
	border-radius: 100%;
	background-color: white;
	box-shadow: inset 0px 0px 1px 0px get-color($border, 'default');
	width: 16px;
	height: 16px;
	transform: translateX(0%);
	transition: transform .2s;
}

.label {
	@extend %text-small;
	@extend %font-families-ui;
	display: flex;
	flex-direction: column;
	font-weight: 600;
	user-select: none;
	margin-left: 17px;
	line-height: 20px;
	color: get-color($text, 'dark');
	box-sizing: border-box;
	&.disabled {
		color: get-color($text, 'muted');
	}
	&.before {
		margin-left: 0;
		margin-right: 17px;
	}

	.info-text {
		margin-left: 5px;
		font-weight: 400;
		line-height: 12px;
		color: get-color($text, 'muted');
	}
}
</style>
